import ApiHelper from '../../ApiHelper';
import API_ENDPOINTS from '../../endpoints';

class SMEContentHelper {
  /**
   * Get all courses assigned to the SME
   * GET /api/v1/sme/courses
   */
  async getMyCourses(params = {}) {
    try {
      const response = await ApiHelper.get(API_ENDPOINTS.SME.COURSES, params);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch courses: ${error.message}`);
    }
  }

  /**
   * Get all contents for a specific course
   * GET /api/v1/sme/courses/:courseId/contents
   * @param {string} courseId - The course ID
   * @param {object} filters - Optional filters (status, type, page, limit)
   */
  async getCourseContents(courseId, filters = {}) {
    try {
      const endpoint = API_ENDPOINTS.SME.COURSE_CONTENTS.replace(':courseId', courseId);
      const response = await ApiHelper.get(endpoint, filters);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch course contents: ${error.message}`);
    }
  }

  /**
   * Create new content for a course
   * POST /api/v1/sme/courses/:courseId/contents
   * @param {string} courseId - The course ID
   * @param {object} contentData - Content data (title, type, content_url, etc.)
   */
  async createContent(courseId, contentData) {
    try {
      const endpoint = API_ENDPOINTS.SME.COURSE_CONTENTS.replace(':courseId', courseId);
      const response = await ApiHelper.post(endpoint, contentData);
      return response;
    } catch (error) {
      throw new Error(`Failed to create content: ${error.message}`);
    }
  }

  /**
   * Update existing content (only if draft or rejected)
   * PUT /api/v1/sme/contents/:contentId
   * @param {string} contentId - The content ID
   * @param {object} contentData - Updated content data
   */
  async updateContent(contentId, contentData) {
    try {
      const endpoint = API_ENDPOINTS.SME.UPDATE_CONTENT.replace(':contentId', contentId);
      const response = await ApiHelper.put(endpoint, contentData);
      return response;
    } catch (error) {
      throw new Error(`Failed to update content: ${error.message}`);
    }
  }

  /**
   * Delete content (only if draft)
   * DELETE /api/v1/sme/contents/:contentId
   * @param {string} contentId - The content ID
   */
  async deleteContent(contentId) {
    try {
      const endpoint = API_ENDPOINTS.SME.DELETE_CONTENT.replace(':contentId', contentId);
      const response = await ApiHelper.delete(endpoint);
      return response;
    } catch (error) {
      throw new Error(`Failed to delete content: ${error.message}`);
    }
  }

  /**
   * Get content statistics for SME
   * GET /api/v1/sme/contents/statistics?courseId=xxx
   * @param {string} courseId - Optional course ID filter
   */
  async getContentStatistics(courseId = null) {
    try {
      const params = courseId ? { courseId } : {};
      const response = await ApiHelper.get(API_ENDPOINTS.SME.CONTENT_STATISTICS, params);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch content statistics: ${error.message}`);
    }
  }

  /**
   * Get single content by ID
   * GET /api/v1/sme/contents/:contentId
   * @param {string} contentId - The content ID
   */
  async getContentById(contentId) {
    try {
      const endpoint = API_ENDPOINTS.SME.GET_CONTENT.replace(':contentId', contentId);
      const response = await ApiHelper.get(endpoint);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch content: ${error.message}`);
    }
  }

  /**
   * Upload file for content
   * POST /api/v1/sme/courses/:courseId/upload
   * @param {string} courseId - The course ID
   * @param {FormData} formData - Form data with file
   */
  async uploadContentFile(courseId, formData) {
    try {
      const endpoint = API_ENDPOINTS.SME.UPLOAD_CONTENT.replace(':courseId', courseId);
      const response = await ApiHelper.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response;
    } catch (error) {
      throw new Error(`Failed to upload file: ${error.message}`);
    }
  }

  /**
   * Get students enrolled in a course
   * GET /api/v1/sme/courses/:courseId/students
   * @param {string} courseId - The course ID
   */
  async getCourseStudents(courseId) {
    try {
      const endpoint = API_ENDPOINTS.SME.COURSE_STUDENTS.replace(':courseId', courseId);
      const response = await ApiHelper.get(endpoint);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch course students: ${error.message}`);
    }
  }

  /**
   * Create notification for course
   * POST /api/v1/sme/courses/:courseId/notifications
   * @param {string} courseId - The course ID
   * @param {object} notificationData - Notification data (title, message, type)
   */
  async createNotification(courseId, notificationData) {
    try {
      const endpoint = API_ENDPOINTS.SME.CREATE_NOTIFICATION.replace(':courseId', courseId);
      const response = await ApiHelper.post(endpoint, notificationData);
      return response;
    } catch (error) {
      throw new Error(`Failed to create notification: ${error.message}`);
    }
  }

  /**
   * Get all notifications
   * GET /api/v1/sme/notifications
   */
  async getAllNotifications() {
    try {
      const response = await ApiHelper.get(API_ENDPOINTS.SME.NOTIFICATIONS);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch notifications: ${error.message}`);
    }
  }

  /**
   * Delete notification
   * DELETE /api/v1/sme/notifications/:notificationId
   * @param {string} notificationId - The notification ID
   */
  async deleteNotification(notificationId) {
    try {
      const endpoint = API_ENDPOINTS.SME.DELETE_NOTIFICATION.replace(':notificationId', notificationId);
      const response = await ApiHelper.delete(endpoint);
      return response;
    } catch (error) {
      throw new Error(`Failed to delete notification: ${error.message}`);
    }
  }
}

export default new SMEContentHelper();
